import { ComponentProps, useCallback, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { CancelButton } from 'src/components/Buttons';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { ApiException, PurchaseClient } from 'src/WebApiController';

export type RequestRetransferDialogProps = ComponentProps<typeof RSModal> & {
  purchaseOrderId: number;
  transactionId: string;
  onSave: () => void;
  onCancel: () => void;
};

export function RequestRetransferDialog({
  purchaseOrderId,
  transactionId,
  onSave,
  onCancel,
  ...dialogProps
}: RequestRetransferDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const unexpectedError = useContent(ContentId.UnexpectedErrorOccured);

  const onInternalSubmit = useCallback(async () => {
    await tryInvokeApi(
      async () => {
        setIsSubmitting(true);

        const result = await new PurchaseClient(
          activeAccountWebClientConfig
        ).requestRetransfer(purchaseOrderId);

        if (!isSuccess(result)) {
          showErrorDialog(
            'PurchaseClient.requestRetransfer',
            {
              message: result?.message ?? unexpectedError,
              status: result?.status!,
            } as ApiException,
            {
              onDismissError: () => onCancel(),
              trackErrorData: { purchaseOrderId },
            }
          );
          return;
        }

        onSave();
      },
      (error) => {
        showErrorDialog('PurchaseClient.requestRetransfer', error, {
          trackErrorData: { purchaseOrderId },
        });
      },
      () => setIsSubmitting(false)
    );
  }, [
    activeAccountWebClientConfig,
    unexpectedError,
    onCancel,
    onSave,
    purchaseOrderId,
    showErrorDialog,
  ]);

  return (
    <GenericDialog
      size="md"
      header={<Content id={ContentId.RequestRetransfer} />}
      footer={
        <>
          <CancelButton disabled={isSubmitting} onClick={onCancel} />
          <Button
            disabled={isSubmitting}
            variant={'regular'}
            onClick={() => onInternalSubmit()}
          >
            <Content id={ContentId.Confirm} />
          </Button>
        </>
      }
      {...dialogProps}
      onCancel={onCancel}
    >
      {isSubmitting ? (
        <PosSpinner />
      ) : (
        <FormatContent
          id={FormatContentId.RequestRetransferInfoMessage}
          params={transactionId}
        />
      )}
    </GenericDialog>
  );
}
